import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BriefcaseBusiness,
  CircleHelp,
  Globe2,
  LogOut,
  MessageSquareWarning,
  Monitor,
  MoreHorizontal,
  Newspaper,
  Settings,
  ShieldCheck,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LanguageToggle } from '@/components/common/language-toggle';
import { ThemeToggle } from '@/components/common/theme-toggle';
import { LOGIN_PATH, useLogout, usePermissions, useSession } from '@/features/auth';
import { NotificationCenter } from '@/features/notifications';
import { GlobalSearch } from '@/features/search';
import { useProductNewsUnreadCount } from '@/features/product-news';
import { useSalesCockpitSummary } from '@/features/sales-assistant';
import { useFeatureAvailability } from '@/features/platform-features';

function Count({ count }: { count: number }): JSX.Element | null {
  return count > 0 ? (
    <Badge className="ms-auto shrink-0 px-1.5" variant="secondary">
      {count > 99 ? '99+' : count}
    </Badge>
  ) : null;
}

export function HeaderActions({
  onHelp,
  onFeedback,
}: {
  onHelp: () => void;
  onFeedback: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const features = useFeatureAvailability();
  const user = useSession().data;
  const logout = useLogout();
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const [preference, setPreference] = useState<'language' | 'theme' | null>(null);
  const newsEnabled = features.isEnabled('product_news');
  const news = useProductNewsUnreadCount(newsEnabled);
  const newsCount = newsEnabled ? (news.data?.unreadCount ?? 0) : 0;
  const salesEnabled =
    features.isEnabled('sales_assistant') &&
    canAccess('SALES_ASSISTANT', 'USE') &&
    canAccess('SALES_ASSISTANT', 'VIEW_OWN_TASKS');
  const sales = useSalesCockpitSummary('OWN', salesEnabled);
  const taskCount = salesEnabled
    ? (sales.data?.overdueTasks ?? 0) + (sales.data?.todayCalls ?? 0)
    : 0;
  const actions = [
    {
      key: 'appearance',
      icon: Monitor,
      label: t('appearance.title'),
      count: 0,
      run: () => navigate('/settings/appearance'),
    },
    ...(newsEnabled
      ? [
          {
            key: 'news',
            icon: Newspaper,
            label: t('productNews.title'),
            count: newsCount,
            run: () => navigate('/news'),
          },
        ]
      : []),
    ...(salesEnabled
      ? [
          {
            key: 'sales',
            icon: BriefcaseBusiness,
            label: t('header.salesTasks'),
            count: taskCount,
            run: () => navigate('/sales-assistant'),
          },
        ]
      : []),
    ...(features.isEnabled('feedback')
      ? [
          {
            key: 'feedback',
            icon: MessageSquareWarning,
            label: t('feedback.open'),
            count: 0,
            run: onFeedback,
          },
        ]
      : []),
    { key: 'help', icon: CircleHelp, label: t('help.open'), count: 0, run: onHelp },
    {
      key: 'settings',
      icon: Settings,
      label: t('nav.settings'),
      count: 0,
      run: () => navigate('/settings'),
    },
  ];

  return (
    <>
      {features.isEnabled('global_search') ? <GlobalSearch /> : null}
      <div className="hidden items-center gap-1 xl:flex">
        {actions.map(({ key, icon: Icon, label, count, run }) => (
          <Button
            key={key}
            variant="ghost"
            size="icon"
            className="relative h-11 w-11 shrink-0 md:h-11 md:w-11"
            aria-label={label}
            title={label}
            onClick={run}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
            {count > 0 ? (
              <span className="absolute end-0 top-0">
                <Count count={count} />
              </span>
            ) : null}
          </Button>
        ))}
      </div>
      {features.isEnabled('notifications') ? <NotificationCenter /> : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-11 w-11 shrink-0 md:h-11 md:w-11"
            ref={moreTrigger}
            aria-label={t('header.more')}
            title={t('header.more')}
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            {newsCount + taskCount > 0 ? (
              <span className="absolute end-0 top-0 xl:hidden">
                <Count count={newsCount + taskCount} />
              </span>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="bottom"
          avoidCollisions={false}
          className="max-h-[calc(100dvh-var(--operating-banner-height,0px)-4.5rem-env(safe-area-inset-bottom))] w-[min(20rem,calc(100vw-1rem))] overflow-y-auto overscroll-contain [&_[role=menuitem]]:min-h-11 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:shrink-0"
        >
          <div className="xl:hidden">
            <DropdownMenuLabel>{t('header.actions')}</DropdownMenuLabel>
            {actions.map(({ key, icon: Icon, label, count, run }) => (
              <DropdownMenuItem key={key} onSelect={run}>
                <Icon aria-hidden="true" />
                <span className="min-w-0 break-words">{label}</span>
                <Count count={count} />
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </div>
          <DropdownMenuLabel>{t('header.preferences')}</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setPreference('language')}>
            <Globe2 aria-hidden="true" />
            {t('language.label')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPreference('theme')}>
            <Monitor aria-hidden="true" />
            {t('theme.label')}
          </DropdownMenuItem>
          {user ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="break-words">
                {user.displayName}
                <span className="block font-normal">{user.tenantName}</span>
              </DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
                <User aria-hidden="true" />
                {t('nav.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/settings/security')}>
                <ShieldCheck aria-hidden="true" />
                {t('nav.security')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={logout.isPending}
                onSelect={() =>
                  logout.mutate(undefined, {
                    onSettled: () => navigate(LOGIN_PATH, { replace: true }),
                  })
                }
              >
                <LogOut aria-hidden="true" />
                {t('auth.logout')}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={preference !== null}
        onOpenChange={(open) => {
          if (!open) setPreference(null);
        }}
      >
        <DialogContent
          className="[&>button]:h-11 [&>button]:w-11"
          closeLabel={t('common.close')}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            moreTrigger.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {t(preference === 'language' ? 'language.label' : 'theme.label')}
            </DialogTitle>
            <DialogDescription>{t('header.preferences')}</DialogDescription>
          </DialogHeader>
          {preference === 'language' ? <LanguageToggle /> : <ThemeToggle showLabels />}
        </DialogContent>
      </Dialog>
    </>
  );
}
